/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：HTTPクライアント
 *
 * 作成者：2021/11/19 ノア）黄
 * 最終修正者：2021/11/19 ノア）黄
 *
 */
import { Vue } from 'vue-property-decorator'
import axios, { AxiosResponse, AxiosError, Canceler, InternalAxiosRequestConfig } from 'axios'

import config from '@/config/config'
import { i18n } from '@/locales'
import router from '@/router'
import { loadingService } from '@/commons/services/loadingService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { moveService } from '@/commons/services/moveService'
import { ResponseData, CommonResponse, FileDownloadResponse } from '@/models'
import { getCookie, setCookie, updateCookieExpires } from '@/utils/cookieUtils'
import { AccessToken } from '@/commons/constants'

/**
 * HTTPクライアント
 */
export const apiClient = axios.create({
  baseURL: config.api.baseUrl,
  timeout: 3600000
})

/**
 * リクエストを取り消した場合に渡すメッセージ
 */
const CANCELED_BY_INTERCEPTORS = 'CANCELED_BY_INTERCEPTORS'

/**
 * TOKENチェックをスキップするURLリスト
 */
const PASS_TOKEN_CHECK_LIST = [config.api.login, config.api.verification, config.api.getAnnouncementLoginListData]

/**
 * loadingの処理は不要なURLリスト
 */
const PASS_LOADING_CHECK_LIST = [config.api.refreshToken]

/**
 * リクエストの前処理
 */
apiClient.interceptors.request.use(
  (cfg: InternalAxiosRequestConfig) => {
    if (!cfg || !cfg.headers) {
      return cfg
    }

    if (!cfg.headers['Content-Type']) {
      if (cfg.data instanceof FormData) {
        cfg.headers['Content-Type'] = 'multipart/form-data'
      } else {
        cfg.headers['Content-Type'] = 'application/json; charset=UTF-8'
      }
    }

    let message = i18n.t('messages.infoDataProcessing').toString()
    if (cfg.method === 'get') {
      message = i18n.t('messages.infoDataLoading').toString()
    }
    if (PASS_LOADING_CHECK_LIST.indexOf(cfg.url as string) === -1) {
      loadingService.open(message)
    }

    const token = getCookie(AccessToken)
    let cancel: Canceler = () => {
      return
    }

    cfg.cancelToken = new axios.CancelToken((canceler: Canceler) => {
      cancel = canceler
    })

    Vue.$log.debug('apis > request: token=', token)
    if (token) {
      cfg.headers['Authorization'] = token
    } else {
      if (PASS_TOKEN_CHECK_LIST.indexOf(cfg.url as string) === -1) {
        cancel(CANCELED_BY_INTERCEPTORS)
      }
    }

    return cfg
  },
  (error: Error) => {
    return error
  }
)

/**
 * レスポンスの前処理
 */
apiClient.interceptors.response.use(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (response: any) => {
    loadingService.close()
    updateCookieExpires(AccessToken)
    const headers = response.headers
    const contentType = headers['content-type']

    if (!contentType.startsWith('application/octet-stream')) {
      response.data = JSON.parse(decodeURIComponent(JSON.stringify(response.data)))
      return Promise.resolve(response)
    }

    const contentDisposition = headers['content-disposition']
    let filename = ''
    if (contentDisposition) {
      const decodeFileName = decodeURI(contentDisposition)
      const matches = decodeFileName.split(';')[1].split('filename=')[1]
      filename = matches ? matches : ''
    }

    const file = {
      fileName: filename,
      data: response.data
    } as FileDownloadResponse

    return Promise.resolve(file)
  },
  (error: AxiosError) => {
    loadingService.close()

    if (error.message === CANCELED_BY_INTERCEPTORS) {
      moveToLogin()
      return Promise.reject(error)
    }
    const res = error.response as AxiosResponse<ResponseData>
    if (!res) {
      msgBoxService.error(i18n.t('messages.errNetwork').toString())
      return Promise.reject(error)
    }

    res.data = JSON.parse(decodeURIComponent(JSON.stringify(res.data)))
    switch (res.status) {
      case 400:
      case 404:
      case 405:
      case 500:
        changeBlobToJson(res).then(commonResponse => {
          msgBoxService.error(getMessage(commonResponse))
        })
        return Promise.reject(res)
      case 401:
        setCookie(AccessToken, '')
        moveToLogin()
        changeBlobToJson(res).then(commonResponse => {
          msgBoxService.error(getMessage(commonResponse), () => {
            msgBoxService.closeAll()
            loadingService.close()
          })
        })
        return Promise.reject(res)
      default:
        msgBoxService.error(i18n.t('messages.common.systemError').toString())
        return Promise.reject(res)
    }
  }
)
/**
 * エラーが発生する場合
 *
 * @param {AxiosResponse<ResponseData>} res レスポンス
 * @returns {CommonResponse} レスポンス
 */
export function changeBlobToJson(res: AxiosResponse<ResponseData>): Promise<CommonResponse> {
  return new Promise(resolve => {
    // ダウンロードの場合、BlobをJsonに変換する
    if (res.data instanceof Blob && res.data.type === 'application/json') {
      const fileReader = new FileReader()
      fileReader.readAsText(res.data, 'utf-8')
      fileReader.onload = () => {
        const commonResponse = JSON.parse(fileReader.result as string)
        resolve(commonResponse)
      }
      // それ以外の場合はJsonを返却する
    } else {
      resolve(res.data)
    }
  })
}

/**
 * メッセージを取得する
 *
 * @param {CommonResponse} commonResponse レスポンス
 * @returns {string} メッセージ
 */
function getMessage(commonResponse: CommonResponse): string {
  if (!commonResponse || !commonResponse.common) {
    return i18n.t('messages.common.systemError').toString()
  }

  if (commonResponse.common.detailMessage && commonResponse.common.detailMessage.length > 0) {
    return messageDisp(commonResponse.common.detailMessage)
  }

  return commonResponse.common.message
}

/**
 * 詳細メッセージを処理する
 *
 * @param {Array<string>} detailMessage 詳細メッセージ
 * @returns {string} メッセージ
 */
function messageDisp(detailMessage: Array<string>): string {
  let allMessage = ''
  if (!detailMessage || detailMessage.length === 0) {
    return allMessage
  }

  allMessage = allMessage ? allMessage + '\r\n' : ''
  for (let i = 0; i < detailMessage.length; i++) {
    allMessage = allMessage + detailMessage[i]
    if (i !== detailMessage.length - 1) {
      allMessage = allMessage + '\r\n'
    }
  }

  return allMessage
}

/**
 * ログイン画面へ遷移する。
 *
 * @returns {void}
 */
function moveToLogin(): void {
  moveService.beforePageChange = undefined

  if (router.currentRoute.path === config.menu.login) {
    return
  }

  router.push(config.menu.login)
}
